import {
  add,
  endOfDay,
  formatISO,
  isAfter,
  isValid,
  startOfDay,
  startOfToday,
} from 'date-fns';
import PropTypes from 'prop-types';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, FormHelperText, IconButton, InputAdornment } from '@mui/material';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';

import { useMuiPickerLocale, useTransl } from 'shared/lib/i18n';

const SingleDatePickerComponent = (props) => {
  const { t } = useTransl();
  const { localeText, adapterLocale } = useMuiPickerLocale();
  const {
    input: { value, onChange },
    meta: { touched, error },
    shouldBePermanent,
    isValidFrom,
    disablePast,
    disabled,
    showCalendarIcon,
    disableOneYearRange,
    isISOString = false,
  } = props;

  const onValidFromChange = (date) => {
    if (!date) return onChange(null);

    const newDate = isValid(date) ? formatISO(startOfDay(date)) : 'invalid';
    return onChange(newDate);
  };

  const onValidUntilChange = (date) => {
    if (!date) return onChange(null);

    const isOutOfOneYearRange = disableOneYearRange
      ? false
      : isAfter(date, add(startOfToday(), { years: 1 }));

    if (isOutOfOneYearRange && isValid(date)) {
      shouldBePermanent();
      return onChange(null);
    }

    const newDate = isValid(date) ? formatISO(endOfDay(date)) : 'invalid';
    return onChange(newDate);
  };

  const onDateChange = (date) => {
    if (isValidFrom) return onValidFromChange(date);
    if (disablePast) return onValidUntilChange(date);
    if (!date) return onChange(null);
    if (!isValid(date)) return 'invalid';

    const newDate = isISOString
      ? new Date(date).toISOString()
      : formatISO(date);

    return onChange(newDate);
  };

  const hasError = touched && !!error;

  const textField = (params) => (
    <TextField
      size="small"
      error={hasError}
      {...params}
      InputProps={{
        startAdornment: showCalendarIcon && (
          <InputAdornment position="start">
            <CalendarMonthIcon />
          </InputAdornment>
        ),
        endAdornment: value && (
          <InputAdornment position="end">
            <IconButton
              aria-label={t('ariaLabel.clear')}
              onClick={(event) => {
                event.stopPropagation();
                onDateChange(null);
              }}
              edge="end"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <Box>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        localeText={localeText}
        adapterLocale={adapterLocale}
      >
        <MobileDatePicker
          value={value ? new Date(value) : null}
          onChange={onDateChange}
          slots={{ textField }}
          disabled={disabled}
          disablePast={disablePast}
        />
      </LocalizationProvider>
      {hasError && (
        <Box mx="1.4rem">
          <FormHelperText error>{error}</FormHelperText>
        </Box>
      )}
    </Box>
  );
};

SingleDatePickerComponent.defaultProps = {
  shouldBePermanent: () => {},
  disablePast: false,
};

SingleDatePickerComponent.propTypes = {
  input: PropTypes.instanceOf(Object).isRequired,
  meta: PropTypes.instanceOf(Object).isRequired,
  disabled: PropTypes.bool,
  shouldBePermanent: PropTypes.func,
  isValidFrom: PropTypes.bool, // true for visitors
  disablePast: PropTypes.bool, // true for visitors and brokers
  showCalendarIcon: PropTypes.bool,
  disableOneYearRange: PropTypes.bool,
  isISOString: PropTypes.bool,
};

export default SingleDatePickerComponent;
